import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../../../../context/AuthContext';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../../../../components/FileAttachment/FileAttachment';
import CommonDatePicker from '../../../../../components/DatePicker/CommonDatePicker';

function SafetyApprovalModal({
  mode, initialData, onClose, onSaveDraft, onUpdateDoc, onSubmitDoc, onApprove, onReject, onDeleteDoc, onStartEdit,
  onOpenApprovalModal, currentDrafter, currentReviewer, currentReviewerPosition, currentApprover, currentApproverPosition, hasCustomizedLine, onWithdrawDoc
}) {
  const { user } = useContext(AuthContext);
  const userDisplayName = user?.UserName || user?.name || '박학송';
  const userPosition = user?.position || '부장';

  const isView = mode === 'DETAIL';

  // 작성(WRITE) 모드 폼 상태
  const [formTitle, setFormTitle] = useState('');
  const [formDraftDate, setFormDraftDate] = useState(new Date().toISOString().split('T')[0]); // 작성일
  const [formPublicScope, setFormPublicScope] = useState('부서'); // 공개 범위
  const [formRetentionPeriod, setFormRetentionPeriod] = useState('3년'); // 보존 연한
  const [formContent, setFormContent] = useState('');
  const [editorHeight, setEditorHeight] = useState('400px');
  const [attachments, setAttachments] = useState([]);
  const [configPrefix, setConfigPrefix] = useState(''); // DB에서 불러온 초기 양식 포맷

  // 상세(DETAIL) 모드 상태
  const [opinionMode, setOpinionMode] = useState(null); // 'approve' | 'reject' | null
  const [opinionComment, setOpinionComment] = useState('');

  const editor = useEditorWithDragDrop(tiptapExtensions);
  useEffect(() => {
    if (initialData) {
      setFormTitle(initialData.title || '');
      setFormDraftDate(initialData.draftDate || new Date().toISOString().split('T')[0]);
      setFormPublicScope(initialData.publicScope || '부서');
      setFormRetentionPeriod(initialData.retentionPeriod || '3년');
      setFormContent(initialData.content || '');
      setAttachments(initialData.attachments || []);

      if (editor && !isView) {
        editor.commands.setContent(initialData.content || '');
      }
    } else {
      // 신규 기안일 경우 초기 설정 포맷(Prefix) 호출
      fetch('/api/safety/aprv/config/prefix')
        .then(res => res.json())
        .then(data => {
          if (data && data.prefix) {
            setConfigPrefix(data.prefix);
          }
        })
        .catch(err => console.error('문서 번호 포맷 조회 실패:', err));
    }
  }, [initialData, editor, isView]);

  const handleSave = (isSubmit) => {
    if (!formTitle.trim()) return alert('제목을 입력해 주세요.');

    if (isSubmit) {
      // 상신 시 결재선 최소 1명(검토자 또는 승인자) 지정 여부 필수 체크
      const hasReviewer = currentReviewer && currentReviewer !== '미지정';
      const hasApprover = currentApprover && currentApprover !== '미지정';
      if (!hasReviewer && !hasApprover) {
        return alert('결재선을 지정해 주세요. (검토자 또는 승인자 최소 1명 이상 필요)');
      }
    }

    const docData = {
      ...(initialData || {}),
      title: formTitle,
      // 문서번호: 기존 번호가 있으면 유지, 없으면 API로 불러온 configPrefix(예: APP-2026-) 저장
      docNumber: initialData?.docNumber || configPrefix || null,
      draftDate: formDraftDate,
      publicScope: formPublicScope,
      retentionPeriod: formRetentionPeriod,
      content: editor ? editor.getHTML() : formContent,
      attachments: attachments,
    };

    if (isSubmit) {
      onSubmitDoc(docData);
    } else {
      const isDraftDoc = initialData && (initialData.doc_status === 'draft' || initialData.doc_status === 'withdraw' || initialData.doc_status === 'reject' || initialData.docStatus === 'draft' || initialData.status === '임시저장' || initialData.status === '회수' || initialData.status === '반려됨');
      if (initialData && !isDraftDoc) {
        onUpdateDoc(docData);
      } else {
        onSaveDraft(docData);
      }
    }
  };

  const handleOpinionSubmit = () => {
    if (opinionMode === 'reject' && !opinionComment.trim()) return alert('반려 사유를 입력해 주세요.');
    if (opinionMode === 'approve') onApprove(opinionComment);
    else if (opinionMode === 'reject') onReject(opinionComment);
    setOpinionMode(null);
    setOpinionComment('');
  };

  const isMyDraft = initialData?.lines && (initialData.lines[0]?.name === userDisplayName || initialData.user_id === user?.userid || initialData.user_id === user?.user_id);
  const isDraftDoc = initialData && (initialData.doc_status === 'draft' || initialData.doc_status === 'withdraw' || initialData.doc_status === 'reject' || initialData.docStatus === 'draft' || initialData.status === '임시저장' || initialData.status === '회수' || initialData.status === '반려됨');
  const hasAnyApproved = initialData?.lines?.some((l, idx) => idx > 0 && (l.status === '승인완료' || l.status === '승인' || l.status === '반려'));
  const isDocProgress = initialData?.doc_status === 'progress' || initialData?.docStatus === 'progress' || initialData?.status === '진행중';
  const isRejected = initialData?.doc_status === 'reject' || initialData?.docStatus === 'reject' || initialData?.status === '반려';
  const isMyTurn = initialData?.lines?.find(l => l.name === userDisplayName && l.status === '검토중');

  // 관리자 권한 확인
  const isAdmin = user?.role === 'admin';

  // 오직 결재 진행중(승인 대기)이거나 최종 완료된 경우, 또는 관리자가 '타인의' 문서를 수정 모드로 진입한 경우 단일 '저장' 버튼 노출
  const isSaveOnlyMode = initialData && ((isAdmin && !isMyDraft) || isDocProgress || initialData?.doc_status === 'complete' || initialData?.docStatus === 'complete' || initialData?.status === '결재완료');

  const activeLines = [];
  if (isView || (initialData && initialData.lines && initialData.lines.length > 0 && !hasCustomizedLine)) {
    if (initialData?.lines && initialData.lines.length > 0) {
      initialData.lines.forEach(line => activeLines.push(line));
    } else {
      activeLines.push({ role: '기안', name: initialData?.drafterName || '-', status: '' });
    }
  } else {
    activeLines.push({ role: '기안', name: currentDrafter || userDisplayName, position: userPosition, status: '' });
    if (currentReviewer && currentReviewer !== '미지정') {
      activeLines.push({ role: '검토', name: currentReviewer, position: currentReviewerPosition || '', status: '' });
    }
    if (currentApprover && currentApprover !== '미지정') {
      activeLines.push({ role: '승인', name: currentApprover, position: currentApproverPosition || '', status: '' });
    }
  }

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1100px', width: '95%' }}>
        <div className="modal-content" style={{ position: 'relative', borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

          {/* 헤더 */}
          <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '16px 24px' }}>
            <h5 className="modal-title fw-bold m-0 fs-5">
              <i className={`fas ${isView ? 'fa-file-signature' : 'fa-edit'} me-2`}></i>
              {isView ? '위험성평가 보고서 상세' : (initialData && initialData.id && initialData.id !== 'new' ? '위험성평가 보고서 수정' : '새 위험성평가 보고서 작성')}
            </h5>
            <div className="d-flex align-items-center gap-3">

              <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
            </div>
          </div>

          {/* 바디 */}
          <div className="modal-body" style={{ height: '70vh', minHeight: '500px', overflowY: 'auto', backgroundColor: '#fff', padding: '16px' }}>

            {/* 문서 외곽선 컨테이너 */}
            <div style={{ width: '100%', minHeight: 'auto', margin: '0 auto', backgroundColor: '#fff', border: '1px solid #94a3b8', padding: '24px 32px' }}>

              {/* 상단 타이틀 및 결재선 */}
              <div className="d-flex justify-content-between align-items-start mb-3" style={{ minHeight: '80px' }}>

                {/* 1. 좌측 타이틀 */}
                <div style={{ paddingTop: '10px' }}>
                  <h2 style={{ fontWeight: '800', letterSpacing: '3px', color: '#0f172a', fontSize: '2rem', borderBottom: '4px double #334155', paddingBottom: '8px', margin: 0, display: 'inline-block' }}>위험성평가 보고서</h2>
                </div>

                {/* 2. 우측 결재선 */}
                <div className="d-flex flex-column align-items-end">
                  {!isView && (!initialData || (initialData.doc_status !== 'complete' && (!hasAnyApproved || isRejected))) && (
                    <div className="mb-2 d-flex align-items-center gap-2">
                      {activeLines.length === 1 && (
                        <span className="text-danger" style={{ fontSize: '0.8rem', fontWeight: '500' }}>* 결재선을 지정해 주세요.</span>
                      )}
                      <button type="button" className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.8rem', padding: '4px 12px', borderRadius: '4px', fontWeight: 600 }} onClick={onOpenApprovalModal}>
                        <i className="fas fa-users me-1"></i> 결재선 변경
                      </button>
                    </div>
                  )}
                  {/* 우측 결재 도장 박스 (모던 표 스타일) */}
                  <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                    <table style={{ borderCollapse: 'collapse', textAlign: 'center', fontSize: '0.8rem', width: 'auto', minWidth: '100px', margin: 0 }}>
                      <tbody>
                        <tr style={{ background: '#f8fafc', fontWeight: 600, color: '#475569', height: '28px' }}>
                          <td rowSpan="3" style={{ borderRight: '1px solid #cbd5e1', width: '28px', padding: '0 6px', wordBreak: 'break-all', backgroundColor: '#f1f5f9' }}>결재</td>
                          {activeLines.map((line, idx) => (
                            <td key={`role-${idx}`} style={{ borderRight: idx !== activeLines.length - 1 ? '1px solid #cbd5e1' : 'none', borderBottom: '1px solid #cbd5e1', minWidth: '100px', padding: '0 8px' }}>
                              {line.name ? (line.user_position || line.position || line.rank || '-') : line.role}
                            </td>
                          ))}
                        </tr>
                        <tr style={{ height: '70px', verticalAlign: 'middle' }}>
                          {activeLines.map((line, idx) => {
                            const isDone = line.status === '기안완료' || line.status === '승인완료' || line.status === '반려';
                            return (
                              <td key={`stamp-${idx}`} style={{ borderRight: idx !== activeLines.length - 1 ? '1px solid #cbd5e1' : 'none', borderBottom: '1px solid #cbd5e1', position: 'relative', padding: '6px' }}>
                                {isDone ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      border: `1px solid ${line.status === '반려' ? '#fca5a5' : '#a5b4fc'}`,
                                      backgroundColor: line.status === '반려' ? '#fef2f2' : '#eef2ff',
                                      color: line.status === '반려' ? '#dc2626' : '#4338ca',
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      letterSpacing: '1px'
                                    }}>
                                      {line.status === '반려' ? '반려' : (idx === 0 ? '기안' : (idx === activeLines.length - 1 ? '승인' : '검토'))}
                                    </span>
                                    <span style={{ color: '#1e293b', fontWeight: 600, fontSize: '0.75rem' }}>{line.name}</span>
                                  </div>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>&nbsp;</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        <tr style={{ height: '28px' }}>
                          {activeLines.map((line, idx) => {
                            const isDone = line.status === '기안완료' || line.status === '승인완료' || line.status === '반려';
                            return (
                              <td key={`date-${idx}`} style={{ borderRight: idx !== activeLines.length - 1 ? '1px solid #cbd5e1' : 'none', color: isDone ? '#475569' : '#1e293b', fontSize: '0.75rem' }}>
                                {isDone ? (line.done_date ? line.done_date.substring(0, 10) : '') : line.name}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* 문서 기본 정보 (직각 표 폼 스타일) */}
              <div className="mb-0" style={{ borderTop: '1px solid #cbd5e1', borderLeft: '1px solid #cbd5e1', borderRight: '1px solid #cbd5e1' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #cbd5e1', height: '46px' }}>
                      <th className="text-center" style={{ background: '#f8fafc', width: '15%', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>작성자</th>
                      <td style={{ width: '35%', padding: '10px 12px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                        {isView ? `${initialData?.drafterName} / ${initialData?.drafterPosition}` : `${userDisplayName} / ${userPosition}`}
                      </td>
                      <th className="text-center" style={{ background: '#f8fafc', width: '15%', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>작성부서</th>
                      <td style={{ width: '35%', padding: '10px 12px', color: '#0f172a' }}>
                        {isView ? (initialData?.department || '-') : 'SI1팀'}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #cbd5e1', height: '46px' }}>
                      <th className="text-center" style={{ background: '#f8fafc', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>문서번호</th>
                      <td style={{ padding: '10px 12px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                        {isView ? (initialData?.docNumber || '-') : (configPrefix || `APP-${new Date().getFullYear()}-`)}
                      </td>
                      <th className="text-center" style={{ background: '#f8fafc', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>공개 범위</th>
                      <td style={{ padding: '10px 12px' }}>
                        {isView ? (
                          <span style={{ color: '#0f172a' }}>{initialData?.publicScope || '부서'}</span>
                        ) : (
                          <select className="form-select form-select-sm" style={{ width: '50%', border: '1px solid #cbd5e1', borderRadius: '0', padding: '4px 8px', fontSize: '0.8rem', boxShadow: 'none' }} value={formPublicScope} onChange={e => setFormPublicScope(e.target.value)}>
                            <option value="개인">개인</option>
                            <option value="부서">부서</option>
                            <option value="전체">전체</option>
                          </select>
                        )}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #cbd5e1', height: '46px' }}>
                      <th className="text-center" style={{ background: '#f8fafc', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>작성일</th>
                      <td style={{ padding: '10px 12px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                        {isView ? (initialData?.draftDate || '-') : (
                          <div style={{ position: 'relative', display: 'inline-block', width: '160px' }}>
                            <CommonDatePicker
                              value={formDraftDate ? new Date(formDraftDate) : new Date()}
                              onChange={date => {
                                if (date) {
                                  const yyyy = date.getFullYear();
                                  const mm = String(date.getMonth() + 1).padStart(2, '0');
                                  const dd = String(date.getDate()).padStart(2, '0');
                                  setFormDraftDate(`${yyyy}-${mm}-${dd}`);
                                }
                              }}
                            />
                            <i className="far fa-calendar-alt" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1', pointerEvents: 'none', fontSize: '0.85rem' }}></i>
                          </div>
                        )}
                      </td>
                      <th className="text-center" style={{ background: '#f8fafc', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>보존연한</th>
                      <td style={{ padding: '10px 12px' }}>
                        {isView ? (
                          <span style={{ color: '#0f172a' }}>{initialData?.retentionPeriod || '3년'}</span>
                        ) : (
                          <select className="form-select form-select-sm" style={{ width: '50%', border: '1px solid #cbd5e1', borderRadius: '0', padding: '4px 8px', fontSize: '0.8rem', boxShadow: 'none' }} value={formRetentionPeriod} onChange={e => setFormRetentionPeriod(e.target.value)}>
                            <option value="1년">1년</option>
                            <option value="3년">3년</option>
                            <option value="5년">5년</option>
                            <option value="영구">영구</option>
                          </select>
                        )}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #cbd5e1', height: '46px' }}>
                      <th className="text-center" style={{ background: '#f8fafc', fontWeight: 600, color: '#334155', padding: '10px 12px', borderRight: '1px solid #cbd5e1' }}>제목</th>
                      <td colSpan="3" style={{ padding: '10px 12px' }}>
                        {isView ? (
                          <div style={{ color: '#0f172a', fontSize: '0.8rem' }}>{initialData?.title}</div>
                        ) : (
                          <input type="text" className="form-control form-control-sm" style={{ border: '1px solid #cbd5e1', borderRadius: '0', padding: '6px 8px', fontSize: '0.8rem', boxShadow: 'none' }} value={formTitle} onChange={e => setFormTitle(e.target.value)} />
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 에디터 및 첨부파일 통합 박스 */}
              <div className="mt-4" style={{ padding: '0px' }}>
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-pen-nib me-1 text-primary"></i> 본문 내용
                    </label>
                    {!isView && (
                      <div className="d-flex align-items-center">
                        <span className="me-2" style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>에디터 높이:</span>
                        <select
                          className="form-select form-select-sm text-secondary"
                          style={{ width: '130px', fontSize: '0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                          value={editorHeight}
                          onChange={(e) => setEditorHeight(e.target.value)}
                        >
                          <option value="400px">기본 (400px)</option>
                          <option value="600px">넓게 (600px)</option>
                          <option value="800px">아주 넓게 (800px)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ height: editorHeight, transition: 'height 0.3s ease', minHeight: 0, border: '1px solid #cbd5e1' }}>
                    {isView ? (
                      <div className="post-content bg-white p-3 rounded shadow-sm h-100 overflow-auto" style={{ lineHeight: '1.8' }} dangerouslySetInnerHTML={{ __html: initialData?.content }} />
                    ) : (
                      <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                        {editor && <EditorToolbar editor={editor} />}
                        <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                          <EditorContent editor={editor} className="editor-content h-100" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 첨부파일 영역 (공통 컴포넌트) - 조회 시 첨부 없으면 숨김 */}
                {(!isView || (isView && attachments && attachments.length > 0)) && (
                  <div className="mt-4">
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-paperclip me-1 text-primary"></i> 첨부 파일
                    </label>
                    <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0, border: '1px solid #cbd5e1' }}>
                      <FileAttachment files={attachments} setFiles={setAttachments} mode={isView ? 'view' : 'edit'} />
                    </div>
                  </div>
                )}

              </div> {/* 통합 박스 끝 */}

            </div> {/* A4 Paper 끝 */}

          </div>

          <style>
            {`
              .btn-hover-primary:hover { background-color: #4338ca !important; border-color: #4338ca !important; }
              .btn-hover-secondary:hover { background-color: #475569 !important; border-color: #475569 !important; }
              .btn-hover-light:hover { background-color: #f8fafc !important; color: #0f172a !important; border-color: #94a3b8 !important; }
            `}
          </style>

          {/* 푸터 */}
          <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', padding: '12px 24px', display: 'flex', justifyContent: 'space-between' }}>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              {/* 결재 의견 조회 버튼 (맨 좌측) */}
              {isView && initialData?.opinions && initialData.opinions.length > 0 && (
                <button type="button" className="btn btn-outline-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem', backgroundColor: '#fff', borderColor: '#94a3b8', color: '#475569' }} onClick={() => setOpinionMode('view')}>
                  <i className="fas fa-comment-dots me-1"></i>의견조회
                </button>
              )}
              {/* 왼쪽: Admin 전용 버튼 */}
              {isAdmin && isView && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm px-3"
                    style={{ fontWeight: 'bold', fontSize: '0.8rem' }}
                    onClick={onStartEdit}>
                    수정(관리자)
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm px-3"
                    style={{ fontWeight: 'bold', fontSize: '0.8rem' }}
                    onClick={() => {
                      if (window.confirm('정말 이 문서를 영구 삭제하시겠습니까?\n(삭제된 데이터는 복구할 수 없습니다.)')) {
                        onDeleteDoc(initialData.aprv_id);
                      }
                    }}>
                    삭제(관리자)
                  </button>
                </>
              )}
            </div>
            {isView ? (
              <div className="d-flex" style={{ gap: '8px' }}>
                {/* 기안자 임시저장 건 우측 수정/삭제 버튼 (반려 제외) */}
                {isMyDraft && isDraftDoc && !isRejected && (
                  <>
                    <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onStartEdit}>수정</button>
                    <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => {
                      if (window.confirm('정말 이 임시저장 문서를 삭제하시겠습니까?')) {
                        onDeleteDoc(initialData.aprv_id || initialData.id);
                      }
                    }}>삭제</button>
                  </>
                )}
                {isMyDraft && isDocProgress && !hasAnyApproved && (
                  <button type="button" className="btn btn-sm px-3 text-white" style={{ fontWeight: 'bold', fontSize: '0.8rem', backgroundColor: '#f97316', borderColor: '#f97316' }} onClick={() => {
                    if (window.confirm('기안한 결재를 회수하시겠습니까?\n(회수 시 임시저장 문서로 이동하며 편집할 수 있습니다.)')) {
                      onWithdrawDoc(initialData.aprv_id || initialData.id);
                    }
                  }}>회수</button>
                )}
                {isMyDraft && isRejected && (
                  <>
                    <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => {
                      if (window.confirm('정말 이 반려 문서를 삭제하시겠습니까?')) {
                        onDeleteDoc(initialData.aprv_id || initialData.id);
                      }
                    }}>삭제</button>
                    <button type="button" className="btn btn-primary btn-sm px-3 text-white" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onStartEdit}>재상신 (수정)</button>
                  </>
                )}
                {isMyTurn && (
                  <>
                    <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => setOpinionMode('reject')}>반려</button>
                    <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => setOpinionMode('approve')}>승인</button>
                  </>
                )}
                <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>닫기</button>
              </div>
            ) : (
              <div className="d-flex" style={{ gap: '8px' }}>
                {isSaveOnlyMode ? (
                  <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => handleSave(false)}>저장</button>
                ) : (
                  <>
                    <button type="button" className="btn btn-success btn-sm px-3 text-white" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => handleSave(false)}>임시저장</button>
                    <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={() => handleSave(true)}>상신</button>
                  </>
                )}
                <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>취소</button>
              </div>
            )}
          </div>

          {/* 팝업 오버레이 (의견 입력/조회 모달) - modal-content 안쪽으로 이동 */}
          {opinionMode && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(2px)' }}>
              <div className="bg-white rounded-3 shadow-lg" style={{ width: '520px', maxWidth: '90%', animation: 'fadeIn 0.2s ease-in-out', overflow: 'hidden' }}>
                {/* 팝업 헤더 (회색 배경) */}
                <div className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                  <h5 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: '#334155', fontSize: '1.1rem' }}>
                    <i className="fas fa-comment-dots"></i>
                    {opinionMode === 'approve' ? '결재의견' : (opinionMode === 'reject' ? '결재의견' : '결재의견')}
                  </h5>
                  <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: '1.2rem', color: '#64748b' }} onClick={() => { setOpinionMode(null); setOpinionComment(''); }}></i>
                </div>

                {/* 팝업 바디 (흰색 배경) */}
                <div style={{ padding: '20px' }}>
                  {opinionMode === 'view' ? (
                    <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '4px' }}>
                      {(initialData?.opinions || []).map((op, idx) => (
                        <div key={idx} style={{ marginBottom: idx < (initialData.opinions.length - 1) ? '12px' : '0', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold',
                                backgroundColor: op.action_type === 'approve' ? '#eff6ff' : '#fef2f2',
                                color: op.action_type === 'approve' ? '#1d4ed8' : '#b91c1c',
                                border: `1px solid ${op.action_type === 'approve' ? '#bfdbfe' : '#fecaca'}`
                              }}>
                                {op.action_type === 'approve' ? '승인' : '반려'}
                              </span>
                              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>
                                {op.user_name || op.user_id || '알수없음'}
                                {op.user_position && <span style={{ fontWeight: 400, color: '#64748b', marginLeft: '4px' }}>{op.user_position}</span>}
                                {op.user_dept_name && <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: '4px' }}>({op.user_dept_name})</span>}
                                {op.step_name && <span style={{ fontWeight: 'normal', color: op.step_name.includes('검토') ? '#059669' : (op.step_name.includes('최종') ? '#7c3aed' : '#2563eb'), marginLeft: '6px' }}>[{op.step_name.replace(/ /g, '')}]</span>}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                              {op.create_date ? new Date(op.create_date).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <div style={{ padding: '12px 14px', fontSize: '0.84rem', fontWeight: 'normal', color: op.opinion ? '#1e293b' : '#94a3b8', lineHeight: '1.5', whiteSpace: 'pre-wrap', height: '80px', overflowY: 'auto' }}>
                            {op.opinion || '등록된 의견이 없습니다.'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className="text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                        {opinionMode === 'approve' ? '승인 의견 (선택)' : '반려 의견 (필수)'}
                      </p>
                      <textarea
                        className="form-control opinion-textarea"
                        rows="6"
                        placeholder={opinionMode === 'approve' ? '승인합니다.' : '반려 사유를 상세히 적어주세요.'}
                        value={opinionComment}
                        onChange={e => setOpinionComment(e.target.value)}
                        autoFocus
                        style={{ resize: 'none', borderRadius: '6px', backgroundColor: '#fff', overflowY: 'auto' }}
                      />
                    </>
                  )}
                </div>

                {/* 팝업 푸터 (회색 배경) */}
                <div className="d-flex justify-content-end gap-2" style={{ backgroundColor: '#f1f5f9', padding: '12px 20px', borderTop: '1px solid #e2e8f0' }}>
                  {opinionMode === 'view' ? (
                    <button type="button" className="btn text-white fw-bold" style={{ backgroundColor: '#64748b', borderRadius: '6px', fontSize: '0.9rem', padding: '6px 16px' }} onClick={() => { setOpinionMode(null); setOpinionComment(''); }}>닫기</button>
                  ) : (
                    <>
                      <button type="button" className="btn text-white fw-bold" style={{ backgroundColor: '#64748b', borderRadius: '6px', fontSize: '0.9rem', padding: '6px 16px' }} onClick={() => { setOpinionMode(null); setOpinionComment(''); }}>취소</button>
                      <button type="button" className="btn text-white fw-bold d-flex align-items-center gap-1" style={{ backgroundColor: opinionMode === 'approve' ? '#4f46e5' : '#ef4444', borderRadius: '6px', fontSize: '0.9rem', padding: '6px 16px' }} onClick={handleOpinionSubmit}>
                        <i className="fas fa-check"></i> {opinionMode === 'approve' ? '승인' : '반려'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .opinion-textarea::placeholder {
          color: #cbd5e1 !important;
          font-weight: 400;
        }
      `}</style>
    </div>,
    document.body
  );
}

export default SafetyApprovalModal;
